import { returnAllTradeDetailsFromGenericParams } from "@/lib/agGridColDefs/colDefGlobalHelpers";
import {
  containsHyphenInQuote,
  formatDollarFractionString,
  formatDollarFractionZValueForSmallFractionDisplay,
  returnFractionString,
  ruleOfEights,
} from "@/lib/businessRules/dollarFractionBusinessRule";
import {
  formatValueWithNotation,
  returnDecimalFormatting,
  isPriceNotation,
} from "@/lib/globalFormatterHelper";
import tradeSettingsStore from "@/store/tradeSettingsStore";
import {
  shouldShowReferencePrice,
  getNotationForLeg,
} from "./referencePriceLegHelpers";
import tradeDataStore from "@/store/tradeDataStore";
import { useTheme } from "next-themes";
import clsx from "clsx";
import { StrategyType } from "@/lib/interfaces and Enums/ENUMStrategyType";
import { getFlashStyles, FlashKeyframes } from "@/lib/styles/flashStyles";
import { ReactNode, useEffect, useState } from "react";

/**
 * ReferencePriceLegCellRenderer
 *
 * Displays the reference price (Set Price) value from the Select Panel settings
 * with real-time Zustand updates. Maps the orderPriceValue input to the leg table.
 *
 * Works in conjunction with referencePriceLegValueFormatter for copy/paste support.
 */
const ReferencePriceLegCellRenderer = (params: any) => {
  const { theme } = useTheme();

  const { fullTradeData } = returnAllTradeDetailsFromGenericParams(params);
  const esid = fullTradeData?.esid;
  const blockId = params.data?.blockId ?? params.data?.id;

  // Subscribe to Zustand store for reactive updates, fallback to fullTradeData for historical trades
  const currentTradeData = tradeDataStore((state) => state.tradeData);
  const thisTrade =
    currentTradeData.find((trade) => trade.esid == fullTradeData.esid) ??
    fullTradeData;
  const currentStatus = thisTrade?.status;
  const isOutright = thisTrade?.strategyType == StrategyType.Outright;

  // Subscribe to flash state from store
  const legCellsToFlash = tradeSettingsStore(
    (state) => state.legCellsToFlash,
  );

  // Check if this cell should flash. Only react to entries targeted at the
  // Set Level cell (field === "setLevel") or untargeted ones (field undefined,
  // backward-compatible "match all" semantics on this leg). Quote/cash field
  // entries belong to other renderers and must not leak in here.
  const shouldFlash = legCellsToFlash.some(
    (cell) =>
      cell.esid === esid &&
      cell.blockId == blockId &&
      (cell.field === "setLevel" || cell.field === undefined),
  );

  const [isFlashing, setIsFlashing] = useState(false);

  // Handle flash effect
  useEffect(() => {
    if (shouldFlash) {
      setIsFlashing(true);
    } else {
      setIsFlashing(false);
    }
  }, [shouldFlash]);

  if (!currentStatus) {
    return;
  }

  // Subscribe to Zustand store for reactive updates
  const tradeSettings = tradeSettingsStore(
    (state) => state.tradeSettingsData,
  );

  const thisTradeSettings = tradeSettings.find((trade) => trade.esid === esid);

  const showRef = shouldShowReferencePrice(
    thisTradeSettings?.settings,
    currentStatus,
    isOutright,
  );

  // For Outright trades, get referencePrice from tradeDataStore legs (synced with settings)
  // This ensures the cell updates when orderPriceValue changes in the settings panel
  let displayValue: number | string | null | undefined =
    params.value !== null && params.value !== undefined ? params.value : null;

  if (isOutright && thisTrade?.legs && thisTrade.legs.length > 0) {
    const matchingLeg = thisTrade.legs.find((leg) => leg.blockId === blockId);
    if (matchingLeg?.referencePrice !== undefined) {
      displayValue = matchingLeg.referencePrice;
    }
  }

  // Get notation for this leg - prefer params.data.notation (available from start)
  const notation =
    params.data?.notation || getNotationForLeg(esid, blockId, fullTradeData);

  // Format with notation suffix (e.g., "15bps"), or show notation as placeholder
  // Apply notation-based rounding before displaying
  let formattedDisplay: ReactNode = isPriceNotation(notation) ? "price" : (notation || "");
  if (displayValue !== null && displayValue !== undefined && displayValue !== "") {
    // $fr: if the value is a fraction string, convert Z-value to superscript fraction
    if (
      notation === "$fr" &&
      typeof displayValue === "string" &&
      containsHyphenInQuote(displayValue)
    ) {
      const fractionStr = formatDollarFractionString(displayValue);
      const valueAfterHyphen = returnFractionString(displayValue, false);

      if (valueAfterHyphen.length === 3) {
        // Strip Z digit from display, show as superscript fraction (e.g., "116-02 ³⁄₈ $fr")
        const quoteDisplayValue = fractionStr.slice(0, -1);
        const zValueDecimal = formatDollarFractionZValueForSmallFractionDisplay(displayValue, ruleOfEights);
        formattedDisplay = <>{quoteDisplayValue}{zValueDecimal}</>;
      } else {
        formattedDisplay = fractionStr;
      }
    } else {
      const numValue =
        typeof displayValue === "string" ? parseFloat(displayValue) : displayValue;
      if (!isNaN(numValue)) {
        const roundedValue = returnDecimalFormatting(numValue, notation, true);
        formattedDisplay = roundedValue
          ? formatValueWithNotation(roundedValue, notation)
          : isPriceNotation(notation) ? "price" : (notation || "");
      }
    }
  }

  const isPlaceholder = formattedDisplay === notation || formattedDisplay === "price";

  const flashStyles = getFlashStyles(isFlashing);

  return (
    <>
      <FlashKeyframes isFlashing={isFlashing} />
      <div
        data-testid="referencePriceLegCellContainer"
        className={clsx(
          "justify-center text-center",
          showRef &&
            !isFlashing &&
            "ag-input ag-roundedInputStyle !border-none",
          showRef &&
            !isFlashing &&
            (theme === "light"
              ? "inputCellOtherLight inputCellLight"
              : "inputCellDark"),
          isPlaceholder && "text-gray-400",
        )}
        style={isFlashing ? flashStyles : { width: "100%" }}
      >
        {showRef && formattedDisplay}
      </div>
    </>
  );
};

export default ReferencePriceLegCellRenderer;
